import { boundedErrorText, type ApiErrorInfo } from "@/lib/api";
import { InfoBanner } from "@/components/ui/banner";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { describeRefusal } from "./refusals";

/**
 * The one refusal surface for this page: a reason code, whatever the page knows
 * about it, and the reference support needs.
 *
 * Three properties, all of them the point of the component:
 *
 *  - **The code is always on screen, verbatim.** Copy from `refusals.ts` sits
 *    *above* the code; it never replaces it. An operator pasting the code into
 *    a support thread has to be able to read it off the page.
 *  - **The code is bounded and rendered as text.** `boundedErrorText` truncates
 *    it the way the client truncates every other server string, and it is a
 *    React text node — never HTML, so a server-controlled string can never
 *    become markup.
 *  - **An unregistered code says so.** "This page has no copy for that code" is
 *    a true statement; a plausible-sounding guess about what `AMOUNT_…` meant
 *    would not be.
 */

export function RefusalPanel({
  error,
  className,
  testId,
}: {
  error: ApiErrorInfo;
  className?: string;
  testId?: string;
}) {
  const code = boundedErrorText(error.reasonCode) ?? error.reasonCode;
  const copy = describeRefusal(error.reasonCode);
  return (
    <InfoBanner
      variant="danger"
      className={className}
      title={copy.title}
      >
      <p data-testid={testId} className="whitespace-pre-line">
        {copy.explanation}
      </p>
      <p className="mt-1.5">
        <span className="font-semibold">Reason code: </span>
        <code dir="ltr" className="break-all font-mono">
          {code}
        </code>
      </p>
      {!copy.known && (
        <p className="mt-1">
          This page has no description for that code, so it is shown exactly as the service returned it.
        </p>
      )}
      <SupportDetails reference={error.supportReference} capability={error.capability} compact={false} />
    </InfoBanner>
  );
}
